// client/src/features/equipment/labels.ts — what an installation is called on screen · D-2733.
//
// Seven types have a Romanian label under `enums.EquipmentType.*`. The eighth, `OTHER`, can
// carry the name the landlord gave it („Hotă”, „Pompă de recirculare”), and where it does, that
// name IS the label: the register row, the detail title, the facts table and the calendar all
// read it from here, so no screen shows „Alt echipament” for an installation that has a name.
// An `OTHER` row recorded before the name existed keeps the fallback label.

import type { TFunction } from 'i18next';

import type { EquipmentType } from '@/client';

/** The two fields every equipment projection carries — the list row and the detail alike. */
export interface NamedEquipmentType {
  equipmentType: EquipmentType;
  /** Absent as well as null: the server omits a null property entirely (02 §7.4, D-235). */
  customTypeName?: string | null;
}

/** The landlord's name for an `OTHER` installation, or `null` when it has none. */
export function customTypeName(equipment: NamedEquipmentType): string | null {
  if (equipment.equipmentType !== 'OTHER') return null;
  const name = equipment.customTypeName?.trim() ?? '';
  return name === '' ? null : name;
}

/** The label an installation's type is shown under. */
export function equipmentTypeLabel(equipment: NamedEquipmentType, t: TFunction): string {
  return customTypeName(equipment) ?? t(`enums.EquipmentType.${equipment.equipmentType}`);
}
